/* ProjectsScreen (DESIGN14 §F1.5) — the mini app's home: a Telegram-style
   large title, a search field, project cards with the four-stage PipelineRow,
   create-a-project via the MainButton + a name-prompt Sheet, pull-to-refresh,
   skeletons while loading, and a soft 5s poll while any project has a live
   run so the animated pipeline dots clear themselves. Reuses architectApi —
   no forked endpoints.

   DESIGN24 §T1.1 — CREATE OPENS THE PROJECT. A new project is created to be
   worked on, so the sheet hands the id straight to the project screen (which
   opens on the poster tab's empty state with its «Add sources» action)
   instead of dropping the operator back on a list to hunt for the row they
   just made. While the sheet is up the native MainButton is hidden: it sits
   BELOW the webview and would otherwise glow through the sheet, next to the
   sheet's own Create button. */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronRight, Pencil, Search, ShieldCheck, Unlink2, UsersRound } from 'lucide-react';
import {
  architectApi, approvalsApi, ApprovalQueueItem, ApprovalStep, ApprovalsOverview,
  ApiError, ArchitectClient, ProjectListItem, ProjectOverview, projectCodes,
} from '../../architect/api';
import { normalizeClientName } from '../../architect/clientNames';
import { useAuth } from '../../auth/AuthContext';
import { fmtDate } from '../../architect/utils';
import { useMainButton, haptic, showConfirm } from '../telegram';
import { PipelineRow } from '../components/PipelineRow';
import { Sheet } from '../components/Sheet';

interface Props {
  onOpen: (id: string) => void;
  /** DESIGN40 — a queue card opens the project on the step's own tab. */
  onOpenStep?: (id: string, step: ApprovalStep | 'pricing') => void;
  /** Root shortcuts to the Team and Approvers screens. */
  onTeam?: () => void;
  onApprovers?: () => void;
}

function isAnyRunning(p: ProjectListItem): boolean {
  const r = p.pipeline?.running;
  return (
    Boolean(p.has_active_run) ||
    Boolean(r && (r.architecture || r.estimate || r.proposal))
  );
}

export default function ProjectsScreen({ onOpen, onOpenStep, onTeam, onApprovers }: Props) {
  // null while the first load is in flight (drives the skeletons).
  const [projects, setProjects] = useState<ProjectListItem[] | null>(null);
  const [clients, setClients] = useState<ArchitectClient[] | null>(null);
  const [directoryView, setDirectoryView] = useState<'clients' | 'projects'>('clients');
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [clientError, setClientError] = useState<string | null>(null);
  const [clientOpen, setClientOpen] = useState(false);
  const [clientName, setClientName] = useState('');
  const [clientDomain, setClientDomain] = useState('');
  const [editingClient, setEditingClient] = useState<ArchitectClient | null>(null);
  const [editedClientName, setEditedClientName] = useState('');
  const [renameClientError, setRenameClientError] = useState<string | null>(null);
  const [renamingClient, setRenamingClient] = useState(false);
  const [phaseOpen, setPhaseOpen] = useState(false);
  const [phaseTitle, setPhaseTitle] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkProjectId, setLinkProjectId] = useState('');
  const [linkClientId, setLinkClientId] = useState('');
  const [linkPhaseTitle, setLinkPhaseTitle] = useState('');
  const [editingPhase, setEditingPhase] = useState<ProjectListItem | null>(null);
  const [editedPhaseTitle, setEditedPhaseTitle] = useState('');
  const [mutatingDirectory, setMutatingDirectory] = useState(false);
  const [directoryError, setDirectoryError] = useState<string | null>(null);
  // Only Root requests the per-approver detail; other users load their queue.
  const [overview, setOverview] = useState<ApprovalsOverview | null>(null);
  // DESIGN40 — «Needs your approval»: the caller's own queue, decided
  // server-side with the approve endpoint's rule (never a 403 on tap).
  const [queue, setQueue] = useState<ApprovalQueueItem[]>([]);
  // The queue shows its first few cards; root on a busy stand can have
  // dozens, and the project list must stay one thumb away.
  const [queueAll, setQueueAll] = useState(false);
  const { user: me } = useAuth();
  const [query, setQuery] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  // A failed create belongs INSIDE the sheet, next to the field it is about —
  // the screen-level banner sits behind the backdrop and would never be read.
  const [createError, setCreateError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const refreshingRef = useRef(false);
  const directoryRevision = useRef(0);
  const renamingClientRef = useRef(false);
  const renameClientButton = useRef<HTMLButtonElement>(null);
  const restoreRenameFocus = useRef(false);

  useEffect(() => {
    if (!editingClient && !renamingClient && restoreRenameFocus.current) {
      restoreRenameFocus.current = false;
      renameClientButton.current?.focus();
    }
  }, [editingClient, renamingClient]);

  const load = useCallback(async (quiet = false) => {
    const revision = directoryRevision.current;
    try {
      const [projectResult, clientResult] = await Promise.allSettled([
        architectApi.listProjects(), architectApi.listClients(),
      ]);
      if (revision !== directoryRevision.current) return;
      if (projectResult.status === 'fulfilled') {
        setProjects(projectResult.value.projects || []);
        setLoadError(null);
      } else {
        if (!quiet) setLoadError((projectResult.reason as Error).message || 'Could not load projects');
        setProjects(prev => prev);
      }
      if (clientResult.status === 'fulfilled') {
        setClients(clientResult.value.clients || []);
        setClientError(null);
      } else {
        setClients(prev => prev);
        setClientError((clientResult.reason as Error).message || 'Could not load clients');
        setDirectoryView('projects');
      }
      if (me?.isRoot) {
        try {
          setOverview(await architectApi.getApprovalsOverview());
        } catch {
          setOverview(null);
        }
      } else {
        setOverview(null);
      }
      try {
        setQueue((await approvalsApi.getMine()).items || []);
      } catch {
        setQueue([]);
      }
    } catch (err: unknown) { if (!quiet) setLoadError((err as Error).message || 'Could not load workspace'); }
  }, [me?.isRoot]);

  useEffect(() => {
    void load();
  }, [load]);

  // Soft poll while anything is generating so the dots + chips self-clear.
  const anyRunning = (projects || []).some(isAnyRunning);
  useEffect(() => {
    if (!anyRunning) return undefined;
    const t = window.setInterval(() => void load(true), 5000);
    return () => window.clearInterval(t);
  }, [anyRunning, load]);

  // Pull-to-refresh via native listeners so we can preventDefault the
  // overscroll and drive a spring indicator.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    let startY = 0;
    let pulling = false;
    let dist = 0;
    const onStart = (e: TouchEvent) => {
      if (el.scrollTop <= 0 && !refreshingRef.current) {
        startY = e.touches[0].clientY;
        pulling = true;
        dist = 0;
      }
    };
    const onMove = (e: TouchEvent) => {
      if (!pulling) return;
      const dy = e.touches[0].clientY - startY;
      if (dy <= 0 || el.scrollTop > 0) {
        pulling = false;
        setPull(0);
        return;
      }
      e.preventDefault();
      dist = Math.min(dy * 0.5, 96);
      setPull(dist);
    };
    const onEnd = () => {
      if (!pulling) return;
      pulling = false;
      if (dist >= 60) {
        refreshingRef.current = true;
        setRefreshing(true);
        haptic.impact('medium');
        void load(true).finally(() => {
          refreshingRef.current = false;
          setRefreshing(false);
        });
      }
      setPull(0);
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [load]);

  const openCreate = useCallback(() => {
    haptic.impact('light');
    setName('');
    setCreateError(null);
    setCreateOpen(true);
  }, []);

  const activeClient = (clients || []).find(client => client.id === selectedClientId) || null;
  const clientPhases = activeClient ? (projects || []).filter(project => project.client_id === activeClient.id)
    .sort((a, b) => (a.phase_order ?? Number.MAX_SAFE_INTEGER) - (b.phase_order ?? Number.MAX_SAFE_INTEGER)) : [];
  const unassignedProjects = (projects || []).filter(project => !project.client_id);
  const openNewClient = () => { haptic.impact('light'); setClientName(''); setClientDomain(''); setDirectoryError(null); setClientOpen(true); };
  const openNewPhase = () => { haptic.impact('light'); setPhaseTitle(''); setDirectoryError(null); setPhaseOpen(true); };
  const openLink = (project?: ProjectListItem) => {
    haptic.impact('light'); setDirectoryError(null);
    setLinkProjectId(project?.id || '');
    setLinkClientId(activeClient?.id || '');
    setLinkPhaseTitle(project?.phase_title || project?.name || '');
    setLinkOpen(true);
  };
  const openPhaseRename = (project: ProjectListItem) => {
    haptic.impact('light');
    setDirectoryError(null);
    setEditingPhase(project);
    setEditedPhaseTitle(project.phase_title || project.name);
  };
  const openClientRename = () => {
    if (!activeClient || mutatingDirectory || renamingClientRef.current) return;
    haptic.impact('light');
    setEditingClient(activeClient);
    setEditedClientName(activeClient.name);
    setRenameClientError(null);
  };
  const closeClientRename = () => {
    if (renamingClientRef.current) return;
    restoreRenameFocus.current = true;
    setEditingClient(null);
    setRenameClientError(null);
  };
  const updateClientName = (client: ArchitectClient) => {
    // A poll started before this write must not restore the previous name.
    directoryRevision.current += 1;
    setClients(previous => previous?.map(value => value.id === client.id ? client : value) ?? previous);
    setProjects(previous => previous?.map(project => project.client_id === client.id
      ? { ...project, client_name: client.name } : project) ?? previous);
  };
  const renameClient = async () => {
    if (!editingClient || renamingClientRef.current || mutatingDirectory) return;
    const name = editedClientName.trim();
    if (!name || name.length > 200) { setRenameClientError('Enter a client name up to 200 characters.'); return; }
    if (name === editingClient.name) { closeClientRename(); return; }
    const existing = clients?.find(client => client.id !== editingClient.id
      && normalizeClientName(client.name) === normalizeClientName(name));
    if (existing) { setRenameClientError(`“${existing.name}” already exists. Choose another name.`); return; }
    renamingClientRef.current = true;
    setRenamingClient(true);
    setRenameClientError(null);
    try {
      const result = await architectApi.renameClient(editingClient.id, name, editingClient.name);
      updateClientName(result.client);
      restoreRenameFocus.current = true;
      setEditingClient(null);
      haptic.notify('success');
    } catch (error) {
      const failure = error as ApiError;
      const code = failure.code || failure.body?.error;
      const current = failure.body?.client;
      if (code === 'client_name_changed' && current?.id === editingClient.id) {
        updateClientName(current);
        setEditingClient(current);
        setRenameClientError(`The current name is “${current.name}”. Review your change and save again.`);
      } else setRenameClientError(code === 'client_exists' ? 'This client name already exists. Choose another name.'
        : code === 'invalid_name' ? 'Enter a client name up to 200 characters.'
        : code === 'invalid_expected_name' ? 'Close this form and open it again to load the current client name.'
        : failure.message || 'Could not rename the client. Try again.');
      haptic.notify('error');
    } finally {
      renamingClientRef.current = false;
      setRenamingClient(false);
    }
  };

  // The native action follows the current place in the same workspace.
  useMainButton({
    text: activeClient ? 'Add phase' : directoryView === 'clients' ? 'New client' : 'New project',
    visible: !createOpen && !clientOpen && !phaseOpen && !linkOpen && !editingPhase && !editingClient,
    onClick: activeClient ? openNewPhase : directoryView === 'clients' ? openNewClient : openCreate,
  });

  const createClient = useCallback(async () => {
    if (!clientName.trim() || mutatingDirectory) return;
    const existing = (clients || []).find(client => normalizeClientName(client.name) === normalizeClientName(clientName));
    if (existing) { setDirectoryError(`“${existing.name}” already exists. Open that client instead.`); return; }
    setMutatingDirectory(true); setDirectoryError(null);
    try {
      const result = await architectApi.createClient(clientName.trim(), clientDomain.trim() || undefined);
      haptic.notify('success'); setClientOpen(false); setSelectedClientId(result.client.id); setDirectoryView('clients');
      await load(true);
    } catch (error) { haptic.notify('error'); setDirectoryError((error as Error).message || 'Could not create client.'); }
    finally { setMutatingDirectory(false); }
  }, [clientName, clientDomain, clients, mutatingDirectory, load]);

  const createPhase = useCallback(async () => {
    if (!activeClient || !phaseTitle.trim() || mutatingDirectory) return;
    setMutatingDirectory(true); setDirectoryError(null);
    try {
      const result = await architectApi.createClientPhase(activeClient.id, phaseTitle.trim());
      haptic.notify('success'); setPhaseOpen(false); await load(true); onOpen(result.project.id);
    } catch (error) { haptic.notify('error'); setDirectoryError((error as Error).message || 'Could not add phase.'); }
    finally { setMutatingDirectory(false); }
  }, [activeClient, phaseTitle, mutatingDirectory, load, onOpen]);

  const linkExisting = useCallback(async () => {
    const project = unassignedProjects.find(candidate => candidate.id === linkProjectId);
    const client = (clients || []).find(candidate => candidate.id === linkClientId);
    if (!project || !client || mutatingDirectory) return;
    setMutatingDirectory(true); setDirectoryError(null);
    try {
      await architectApi.linkProjectClient(project.id, {
        client_id: client.id, expected_client_id: project.client_id ?? null,
        phase_title: linkPhaseTitle.trim() || project.name,
      });
      haptic.notify('success'); setLinkOpen(false); setDirectoryView('clients'); setSelectedClientId(client.id);
      await load(true);
    } catch (error) { haptic.notify('error'); setDirectoryError((error as Error).message || 'Could not link the project.'); }
    finally { setMutatingDirectory(false); }
  }, [unassignedProjects, clients, linkProjectId, linkClientId, linkPhaseTitle, mutatingDirectory, load]);

  const renamePhase = useCallback(async () => {
    if (!editingPhase?.client_id || !editedPhaseTitle.trim() || mutatingDirectory) return;
    const title = editedPhaseTitle.trim();
    if (title === (editingPhase.phase_title || editingPhase.name)) { setEditingPhase(null); return; }
    setMutatingDirectory(true); setDirectoryError(null);
    try {
      await architectApi.linkProjectClient(editingPhase.id, {
        client_id: editingPhase.client_id,
        expected_client_id: editingPhase.client_id,
        phase_title: title,
      });
      await load(true);
      setEditingPhase(null);
      haptic.notify('success');
    } catch (error) {
      setDirectoryError((error as Error).message || 'Could not rename the phase.');
      haptic.notify('error');
    } finally { setMutatingDirectory(false); }
  }, [editedPhaseTitle, editingPhase, load, mutatingDirectory]);

  const unlinkPhase = useCallback(async (project: ProjectListItem) => {
    if (!project.client_id || mutatingDirectory) return;
    const confirmed = await showConfirm(`Unlink “${project.phase_title || project.name}” from this client? The architecture, cases, documents and approvals will remain in Projects.`);
    if (!confirmed) return;
    setMutatingDirectory(true); setDirectoryError(null);
    try {
      await architectApi.linkProjectClient(project.id, {
        client_id: null, expected_client_id: project.client_id,
      });
      await load(true);
      haptic.notify('success');
    } catch (error) {
      setDirectoryError((error as Error).message || 'Could not unlink the architecture.');
      haptic.notify('error');
    } finally { setMutatingDirectory(false); }
  }, [load, mutatingDirectory]);

  const create = useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed || creating) return;
    setCreating(true);
    setLoadError(null);
    try {
      const r = await architectApi.createProject(trimmed);
      haptic.notify('success');
      setCreating(false);
      setCreateOpen(false);
      // Straight into the new project — its poster tab opens on the empty
      // state, whose primary action is «Add sources».
      onOpen(r.project.id);
    } catch (err: unknown) {
      haptic.notify('error');
      setCreating(false);
      setCreateError((err as Error).message || 'Could not create the project');
    }
  }, [name, creating, onOpen]);

  const list = projects || [];
  const q = query.trim().toLowerCase();
  const filtered = q ? list.filter(p => [p.name, p.phase_title || '', p.client_name || '', projectCodes(p.number, p.doc_kind, p.pipeline).docCode || ''].some(value => value.toLowerCase().includes(q))) : list;
  const shownClients = q ? (clients || []).filter(client => [client.name, client.code, client.domain || '', ...(projects || []).filter(project => project.client_id === client.id).flatMap(project => [project.name, project.phase_title || '', ...(project.case_titles || [])])].some(value => normalizeClientName(value).includes(normalizeClientName(q)))) : (clients || []);

  const showIndicator = pull > 4 || refreshing;
  const spin = refreshing ? 1 : Math.min(1, pull / 60);
  const readyForMe = queue.filter(item => item.my_turn).length;

  return (
    <div className="tma-screen tma-projects" ref={scrollRef}>
      <div
        className="tma-ptr"
        style={{ height: refreshing ? 44 : pull, opacity: showIndicator ? 1 : 0 }}
      >
        <span
          className={'tma-ptr-spin' + (refreshing ? ' active' : '')}
          style={{ transform: `rotate(${spin * 300}deg)` }}
        />
      </div>

      <div
        className="tma-projects-inner"
        style={{ transform: pull ? `translateY(${pull}px)` : undefined }}
      >
        <div className="tma-directory-heading">
          <div><span className="tma-directory-eyebrow">LATO AI · ARCHITECT</span><div className="tma-client-title-row"><h1 className="tma-title">{activeClient?.name || (directoryView === 'clients' ? 'Clients' : 'Projects')}</h1>{activeClient ? <button ref={renameClientButton} type="button" className="tma-client-rename" aria-label={`Rename client ${activeClient.name}`} title="Rename client" disabled={mutatingDirectory || renamingClient} onClick={openClientRename}><Pencil size={17} aria-hidden="true" /></button> : null}</div></div>
          {activeClient ? <button type="button" className="tma-directory-back" onClick={() => { haptic.impact('light'); setSelectedClientId(null); setQuery(''); }}>‹ Clients</button> : null}
        </div>
        {!activeClient ? <div className="tma-directory-tabs" role="tablist" aria-label="Workspace directory">
          <button type="button" role="tab" aria-selected={directoryView === 'clients'} className={directoryView === 'clients' ? 'active' : ''} onClick={() => { haptic.select(); setDirectoryView('clients'); setQuery(''); }}>Clients <span>{(clients || []).length}</span></button>
          <button type="button" role="tab" aria-selected={directoryView === 'projects'} className={directoryView === 'projects' ? 'active' : ''} onClick={() => { haptic.select(); setDirectoryView('projects'); setQuery(''); }}>Projects <span>{(projects || []).length}</span></button>
        </div> : null}

        {!activeClient && me?.isRoot && (onTeam || onApprovers) ? (
          <nav className="tma-root-tools" aria-label="Workspace management">
            <div className="tma-root-tools-title">Manage workspace</div>
            <div className="tma-root-tools-list">
            {onTeam ? (
              <button type="button" className="tma-root-tool" onClick={() => { haptic.impact('light'); onTeam(); }}>
                <span className="tma-root-tool-icon" aria-hidden="true"><UsersRound size={19} /></span>
                <span className="tma-root-tool-copy"><strong>Team</strong><small>People and access</small></span>
                <ChevronRight size={18} aria-hidden="true" />
              </button>
            ) : null}
            {onApprovers ? (
              <button type="button" className="tma-root-tool" onClick={() => { haptic.impact('light'); onApprovers(); }}>
                <span className="tma-root-tool-icon" aria-hidden="true"><ShieldCheck size={19} /></span>
                <span className="tma-root-tool-copy"><strong>Approvers</strong><small>Approval rules</small></span>
                <ChevronRight size={18} aria-hidden="true" />
              </button>
            ) : null}
            </div>
          </nav>
        ) : null}

        {queue.length && !activeClient ? (
          <section className="tma-queue" aria-label="Approval queue">
            <div className="tma-queue-head">
              <span className="tma-queue-title">Approval queue</span>
              <span className="tma-queue-count">{queue.length}</span>
            </div>
            <p className="tma-queue-intro">{readyForMe ? `${readyForMe} ready for you` : 'Waiting for teammates'}{queue.length > readyForMe && readyForMe ? ` · ${queue.length - readyForMe} waiting` : ''}</p>
            <div className="tma-cards">
              {(queueAll ? queue : queue.slice(0, QUEUE_PEEK)).map((q) => (
                <button
                  type="button"
                  className={'tma-card tma-queue-card' + (q.my_turn ? ' is-my-turn' : '')}
                  key={q.step + ':' + q.target_id}
                  onClick={() => {
                    haptic.impact('light');
                    if (onOpenStep) onOpenStep(q.project_id, q.step);
                    else onOpen(q.project_id);
                  }}
                >
                  <div className="tma-card-top">
                    <span className="tma-card-name">{q.project_name || 'Project'}</span>
                    <span className="tma-card-chevron" aria-hidden="true">›</span>
                  </div>
                  <div className="tma-queue-meta">
                    <span className={'tma-queue-step s-' + q.step}>{QUEUE_STEP_LABEL[q.step]}</span>
                    {q.required > 1 ? (
                      <span className="tma-apprv-count">{q.given} of {q.required}</span>
                    ) : null}
                    <span className={'tma-queue-with' + (q.my_turn ? ' is-my-turn' : '')}>
                      {q.my_turn ? 'Your turn' : q.waiting.length ? `Waiting for ${q.waiting.filter((n) => n !== (me?.displayName || me?.username)).join(', ') || 'teammate'}` : 'Waiting for teammate'}
                    </span>
                  </div>
                </button>
              ))}
            </div>
            {queue.length > QUEUE_PEEK ? (
              <button
                type="button"
                className="tma-queue-more"
                onClick={() => { haptic.impact('light'); setQueueAll((v) => !v); }}
              >
                {queueAll ? 'Show fewer' : `Show all ${queue.length}`}
              </button>
            ) : null}
          </section>
        ) : null}

        <div className="tma-search">
          <span className="tma-search-icon" aria-hidden="true">
            <Search size={18} />
          </span>
          <input
            className="tma-search-input"
            type="text"
            inputMode="search"
            placeholder={activeClient ? 'Search phases or cases' : directoryView === 'clients' ? 'Search clients or phases' : 'Search projects'}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query ? (
            <button
              type="button"
              className="tma-search-clear"
              aria-label="Clear"
              onClick={() => setQuery('')}
            >
              ✕
            </button>
          ) : null}
        </div>

        {loadError ? (
          <div className="tma-banner err" role="alert">
            {loadError}
          </div>
        ) : null}
        {clientError ? <div className="tma-banner warn" role="status">Client directory is unavailable. Your projects are still here.</div> : null}

        {projects === null && loadError ? (
          <div className="tma-empty" role="status"><div className="tma-empty-title">Projects could not load</div><div className="tma-empty-sub">Check your connection and try again.</div><button type="button" className="tma-btn tma-btn-primary" onClick={() => void load()}>Try again</button></div>
        ) : directoryView === 'clients' && !clientError ? (
          clients === null || projects === null ? <div className="tma-cards">{[0, 1, 2].map(i => <div className="tma-card tma-card-skel" key={i}><div className="tma-skel tma-skel-name" /><div className="tma-skel tma-skel-meta" /></div>)}</div>
          : activeClient ? <div className="tma-client-detail">
            {directoryError && !editingPhase ? <div className="tma-banner err" role="alert">{directoryError}</div> : null}
            <div className="tma-client-summary"><span className="tma-client-monogram" aria-hidden="true">{activeClient.name.slice(0, 2).toUpperCase()}</span><div><strong>{activeClient.name}</strong><span>{activeClient.code} · {clientPhases.length} {clientPhases.length === 1 ? 'phase' : 'phases'}</span></div></div>
            {activeClient.domain ? <div className="tma-client-domain">{activeClient.domain}</div> : null}
            <div className="tma-client-actions"><button type="button" onClick={openNewPhase}>+ Add phase</button><button type="button" onClick={() => openLink()}>↗ Link existing</button></div>
            <div className="tma-client-section-title">Phases <span>{clientPhases.length}</span></div>
            {clientPhases.filter(project => !q || [project.name, project.phase_title || '', ...(project.case_titles || [])].some(value => value.toLowerCase().includes(q))).length ? <div className="tma-cards">{clientPhases.filter(project => !q || [project.name, project.phase_title || '', ...(project.case_titles || [])].some(value => value.toLowerCase().includes(q))).map((project, index) => {
              const code = projectCodes(project.number, project.doc_kind, project.pipeline).docCode;
              return <article className="tma-client-phase" key={project.id}>
                <button type="button" className="tma-client-phase-open" onClick={() => { haptic.impact('light'); onOpen(project.id); }}><span className="tma-phase-number">{String(project.phase_order ?? index + 1).padStart(2, '0')}</span><span className="tma-phase-copy"><strong>{project.phase_title || project.name}</strong><small>{code || 'Architecture'} · Updated {fmtDate(project.updated_at)}</small></span><span aria-hidden="true">↗</span></button>
                <PipelineRow pipeline={project.pipeline} variant="full" />
                {overview?.projects[project.id] ? <ApproversLines d={overview.projects[project.id]} /> : null}
                {project.case_titles?.length ? <details className="tma-phase-cases"><summary>Cases <span>{project.case_titles.length}</span></summary>{project.case_titles.map((title, caseIndex) => <div key={caseIndex} className="tma-phase-case"><span>{caseIndex + 1}</span>{title || `Case ${caseIndex + 1}`}</div>)}</details> : null}
                <div className="tma-phase-actions">
                  <button type="button" disabled={mutatingDirectory} onClick={() => openPhaseRename(project)} aria-label={`Rename phase ${project.phase_title || project.name}`}><Pencil size={15} /> Rename</button>
                  <button type="button" disabled={mutatingDirectory} onClick={() => void unlinkPhase(project)} aria-label={`Unlink phase ${project.phase_title || project.name} from ${activeClient.name}`}><Unlink2 size={15} /> Unlink</button>
                </div>
              </article>;
            })}</div> : <div className="tma-empty"><div className="tma-empty-title">{q ? 'No matching phases' : 'Ready for the first phase'}</div><div className="tma-empty-sub">{q ? 'Try another search.' : 'Add a phase or connect an existing architecture.'}</div></div>}
          </div>
          : shownClients.length ? <div className="tma-cards">{shownClients.map(client => {
            const phases = list.filter(project => project.client_id === client.id).sort((a, b) => (a.phase_order ?? 1e9) - (b.phase_order ?? 1e9));
            const waitingPhases = phases.filter(project => {
              const detail = overview?.projects[project.id];
              return detail && [detail.architecture, detail.estimate, detail.proposal].some(stage => stage.state === 'awaiting');
            }).length;
            return <button type="button" className="tma-client-card" key={client.id} onClick={() => { haptic.impact('light'); setSelectedClientId(client.id); setQuery(''); }}>
              <span className="tma-client-card-top"><span className="tma-client-monogram" aria-hidden="true">{client.name.slice(0, 2).toUpperCase()}</span><span className="tma-client-card-name"><strong>{client.name}</strong><small>{client.code} · {phases.length} {phases.length === 1 ? 'phase' : 'phases'}</small></span><span className="tma-client-open" aria-hidden="true">↗</span></span>
              <span className="tma-client-card-phases">{phases.length ? <span><b>{String(phases[0].phase_order ?? 1).padStart(2, '0')}</b><span>{phases[0].phase_title || phases[0].name}</span>{phases.length > 1 ? <small>+{phases.length - 1}</small> : null}</span> : <span>Ready for the first phase</span>}</span>
              {waitingPhases ? <span className="tma-client-card-attention">{waitingPhases} {waitingPhases === 1 ? 'phase' : 'phases'} awaiting review</span> : null}
            </button>;
          })}</div>
          : <div className="tma-empty"><div className="tma-empty-title">{q ? 'No matching clients' : 'Every client starts here'}</div><div className="tma-empty-sub">{q ? 'Try another name, phase or case.' : 'Create a client, then add phases or connect an existing architecture.'}</div>{!q && unassignedProjects.length ? <button type="button" className="tma-btn tma-btn-ghost" onClick={() => setDirectoryView('projects')}>Review {unassignedProjects.length} existing projects</button> : null}</div>
        ) : projects === null ? (
          <div className="tma-cards">
            {[0, 1, 2, 3].map((i) => (
              <div className="tma-card tma-card-skel" key={i}>
                <div className="tma-skel tma-skel-name" />
                <div className="tma-skel tma-skel-meta" />
                <div className="tma-skel tma-skel-pipe" />
              </div>
            ))}
          </div>
        ) : filtered.length ? (
          <div className="tma-cards">
            {filtered.map((p) => {
              const running = isAnyRunning(p);
              // Read-only document code chips (DESIGN20 §F2) — W/TS for the
              // architecture-or-tech-scope doc, muted LP once a proposal
              // exists. Same computed rule as the web surface (one source).
              const codes = projectCodes(p.number, p.doc_kind, p.pipeline);
              return (
                <div className="tma-project-entry" key={p.id}>
                <button
                  type="button"
                  className={'tma-card tma-project-card' + (running ? ' running' : '')}
                  onClick={() => {
                    haptic.impact('light');
                    onOpen(p.id);
                  }}
                >
                  <div className="tma-card-top">
                    <span className="tma-card-name">{p.name}</span>
                    <span className="tma-card-chevron" aria-hidden="true">
                      ›
                    </span>
                  </div>
                  {codes.docCode || codes.proposalCode ? (
                    <div className="tma-card-codes">
                      {codes.docCode ? (
                        <span className="tma-code-chip">{codes.docCode}</span>
                      ) : null}
                      {codes.proposalCode ? (
                        <span className="tma-code-chip muted">
                          {codes.proposalCode}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="tma-card-meta">
                    <span>{fmtDate(p.updated_at) || 'new'}</span>
                    <span className="tma-dot-sep">·</span>
                    <span>
                      {(p.version_count || 0) === 1
                        ? '1 version'
                        : (p.version_count || 0) + ' versions'}
                    </span>
                    {running ? (
                      <span className="tma-gen-chip">
                        <span className="tma-spin tma-gen-spin" />
                        generating
                      </span>
                    ) : null}
                  </div>
                  <PipelineRow pipeline={p.pipeline} variant="full" />
                </button>
                {overview?.projects[p.id] ? <ApproversLines d={overview.projects[p.id]} /> : null}
                <div className="tma-project-entry-foot">
                  {p.client_id ? <span>{p.client_name || 'Linked client'} · {p.phase_title || 'Phase'}</span> : <span>Unassigned architecture</span>}
                  {!p.client_id ? <button type="button" onClick={() => openLink(p)}>Link to client ↗</button> : null}
                </div>
                </div>
              );
            })}
          </div>
        ) : list.length ? (
          <div className="tma-empty">
            <div className="tma-empty-emoji">🔍</div>
            <div className="tma-empty-title">Nothing found</div>
            <div className="tma-empty-sub">Try a different search</div>
          </div>
        ) : (
          <div className="tma-empty">
            <div className="tma-empty-emoji">📁</div>
            <div className="tma-empty-title">No projects yet</div>
            <div className="tma-empty-sub">
              Tap “New project” below to create your first one
            </div>
          </div>
        )}

        <div className="tma-mb-spacer" />
      </div>

      <Sheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New project"
        footer={
          <button
            type="button"
            className="tma-btn tma-btn-primary tma-btn-block"
            disabled={creating || !name.trim()}
            onClick={create}
          >
            {creating ? 'Creating…' : 'Create'}
          </button>
        }
      >
        {createError ? (
          <div className="tma-banner err" role="alert">
            {createError}
          </div>
        ) : null}
        <label className="tma-field-label" htmlFor="tma-new-project-name">
          Name
        </label>
        <input
          id="tma-new-project-name"
          className="tma-input"
          type="text"
          autoFocus
          maxLength={120}
          placeholder="e.g. CRM for a clinic"
          value={name}
          disabled={creating}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && name.trim()) create();
          }}
        />
        <p className="tma-field-hint">
          It opens right away — paste the call transcript in “Sources”, then
          generate the architecture.
        </p>
      </Sheet>

      <Sheet open={clientOpen} onClose={() => { if (!mutatingDirectory) setClientOpen(false); }} title="New client"
        footer={<button type="button" className="tma-btn tma-btn-primary tma-btn-block" disabled={mutatingDirectory || !clientName.trim()}
          onClick={() => void createClient()}>{mutatingDirectory ? 'Creating…' : 'Create client'}</button>}>
        {directoryError ? <div className="tma-banner err" role="alert">{directoryError}</div> : null}
        <label className="tma-field-label" htmlFor="tma-client-name">Client name</label>
        <input id="tma-client-name" className="tma-input" maxLength={200} value={clientName} onChange={event => { setClientName(event.target.value); setDirectoryError(null); }} placeholder="e.g. Health Clinic MD" />
        {(clients || []).filter(client => normalizeClientName(client.name) === normalizeClientName(clientName) && !!clientName.trim()).map(client => <button key={client.id} type="button" className="tma-client-suggestion" onClick={() => { setClientOpen(false); setSelectedClientId(client.id); setDirectoryView('clients'); }}>Already exists: <strong>{client.name}</strong> · Open client ↗</button>)}
        <label className="tma-field-label" htmlFor="tma-client-domain">Website or domain · optional</label>
        <input id="tma-client-domain" className="tma-input" value={clientDomain} onChange={event => setClientDomain(event.target.value)} placeholder="example.com" />
        <p className="tma-field-hint">One client identity holds every phase and its existing documents.</p>
      </Sheet>

      <Sheet open={phaseOpen} onClose={() => { if (!mutatingDirectory) setPhaseOpen(false); }} title="Add phase"
        footer={<button type="button" className="tma-btn tma-btn-primary tma-btn-block" disabled={mutatingDirectory || !phaseTitle.trim()}
          onClick={() => void createPhase()}>{mutatingDirectory ? 'Adding…' : 'Add phase'}</button>}>
        {directoryError ? <div className="tma-banner err" role="alert">{directoryError}</div> : null}
        <p className="tma-field-hint">{activeClient?.name} · Each phase is one project with its own architecture, estimate, pricing and proposal.</p>
        <label className="tma-field-label" htmlFor="tma-phase-title">Phase title</label>
        <input id="tma-phase-title" className="tma-input" maxLength={120} value={phaseTitle} onChange={event => setPhaseTitle(event.target.value)} placeholder="e.g. Communications" />
        <button type="button" className="tma-client-suggestion" onClick={() => { setPhaseOpen(false); openLink(); }}>Already have an architecture? Link it ↗</button>
      </Sheet>

      <Sheet open={linkOpen} onClose={() => { if (!mutatingDirectory) setLinkOpen(false); }} title="Link existing architecture"
        footer={<button type="button" className="tma-btn tma-btn-primary tma-btn-block" disabled={mutatingDirectory || !linkProjectId || !linkClientId}
          onClick={() => void linkExisting()}>{mutatingDirectory ? 'Linking…' : 'Link architecture'}</button>}>
        {directoryError ? <div className="tma-banner err" role="alert">{directoryError}</div> : null}
        <p className="tma-field-hint">Choose an existing project and its client. Cases, files, approvals and documents remain attached to that project.</p>
        <label className="tma-field-label" htmlFor="tma-link-project">Existing architecture</label>
        <select id="tma-link-project" className="tma-input" value={linkProjectId} onChange={event => {
          setLinkProjectId(event.target.value);
          const project = unassignedProjects.find(candidate => candidate.id === event.target.value);
          setLinkPhaseTitle(project?.phase_title || project?.name || '');
        }}><option value="">Choose project</option>{unassignedProjects.map(project => <option key={project.id} value={project.id}>{project.name}</option>)}</select>
        <label className="tma-field-label" htmlFor="tma-link-client">Client</label>
        <select id="tma-link-client" className="tma-input" value={linkClientId} onChange={event => setLinkClientId(event.target.value)}><option value="">Choose client</option>{(clients || []).map(client => <option key={client.id} value={client.id}>{client.name} · {client.code}</option>)}</select>
        <label className="tma-field-label" htmlFor="tma-link-title">Phase title</label>
        <input id="tma-link-title" className="tma-input" maxLength={120} value={linkPhaseTitle} onChange={event => setLinkPhaseTitle(event.target.value)} placeholder="Phase title" />
        {!unassignedProjects.length ? <p className="tma-field-hint">No unassigned architectures remain.</p> : null}
        {!(clients || []).length ? <button type="button" className="tma-client-suggestion" onClick={() => { setLinkOpen(false); openNewClient(); }}>Create a client first ↗</button> : null}
      </Sheet>

      <Sheet open={!!editingClient} onClose={closeClientRename} title="Rename client"
        footer={<div className="tma-client-rename-actions"><button type="button" className="tma-btn" disabled={renamingClient} onClick={closeClientRename}>Cancel</button><button type="submit" form="tma-rename-client-form" className="tma-btn tma-btn-primary" disabled={renamingClient || !editedClientName.trim()}>{renamingClient ? 'Saving…' : 'Save'}</button></div>}>
        <form id="tma-rename-client-form" aria-label="Rename client" aria-busy={renamingClient} onSubmit={event => { event.preventDefault(); void renameClient(); }}>
          <label className="tma-field-label" htmlFor="tma-edit-client-name">Client name</label>
          <input id="tma-edit-client-name" className="tma-input" value={editedClientName} maxLength={200} required autoFocus disabled={renamingClient}
            aria-invalid={!!renameClientError} aria-describedby={renameClientError ? 'tma-rename-client-error' : undefined}
            onChange={event => { setEditedClientName(event.target.value); setRenameClientError(null); }} />
          {renameClientError ? <div id="tma-rename-client-error" className="tma-banner err" role="alert">{renameClientError}</div> : null}
        </form>
      </Sheet>

      <Sheet open={!!editingPhase} onClose={() => { if (!mutatingDirectory) setEditingPhase(null); }} title="Rename phase"
        footer={<button type="button" className="tma-btn tma-btn-primary tma-btn-block" disabled={mutatingDirectory || !editedPhaseTitle.trim()}
          onClick={() => void renamePhase()}>{mutatingDirectory ? 'Saving…' : 'Save phase title'}</button>}>
        {directoryError ? <div className="tma-banner err" role="alert">{directoryError}</div> : null}
        <p className="tma-field-hint">This changes the phase label only. The architecture, cases, estimate, pricing and proposal stay attached.</p>
        <label className="tma-field-label" htmlFor="tma-edit-phase-title">Phase title</label>
        <input id="tma-edit-phase-title" className="tma-input" maxLength={120} value={editedPhaseTitle}
          onChange={event => { setEditedPhaseTitle(event.target.value); setDirectoryError(null); }} />
      </Sheet>
    </div>
  );
}

/* Root's per-approver lines (DESIGN30) — the TMA cut of the web detail:
   who each stage waits for, right on the card. Only mounted when the
   root-only overview actually arrived. */
const QUEUE_PEEK = 4;

const QUEUE_STEP_LABEL: Record<ApprovalStep | 'pricing', string> = {
  architecture: 'Approve the poster',
  estimate: 'Approve the estimate',
  pricing: 'Review and approve pricing',
  proposal: 'Approve the proposal',
};

function ApproversLines({ d }: { d: ProjectOverview }) {
  const dt = (iso: string | null | undefined) => iso ? fmtDate(iso) : '';
  const line = (label: string, s: any, ai: string): React.ReactNode => {
    if (!s || s.state === 'idle') return null;
    if (s.state === 'generating') {
      return (
        <div className="tma-apprv-row" key={label}>
          <span className="tma-apprv-stage">{label}</span>
          <span className="tma-apprv-live">
            <span className="tma-spin tma-apprv-spin" /> {ai}
          </span>
        </div>
      );
    }
    if (s.state === 'approved') {
      return (
        <div className="tma-apprv-row" key={label}>
          <span className="tma-apprv-stage">{label}</span>
          <span className="tma-apprv-done">✓ {s.approved_by || 'approved'}{s.approved_at ? ' · ' + dt(s.approved_at) : ''}</span>
        </div>
      );
    }
    return (
      <div className="tma-apprv-row" key={label}>
        <span className="tma-apprv-stage">{label}</span>
        <span className="tma-apprv-wait">
          {(s.voted || []).map((v: any, i: number) => (
            <span className="tma-apprv-voted" key={i}>✓ {v.name || 'approved'}</span>
          ))}
          <span className="tma-apprv-pending">
            {s.waiting && s.waiting.length
              ? 'awaiting ' + s.waiting.join(', ')
              : s.voted && s.voted.length
                ? 'awaiting approval'
                : 'awaiting any approver'}
          </span>
          {s.required > 1 ? (
            <span className="tma-apprv-count">{s.given} of {s.required}</span>
          ) : null}
        </span>
      </div>
    );
  };
  const pricing = d.pricing.state === 'idle' ? null : (
    <div className="tma-apprv-row" key="pricing">
      <span className="tma-apprv-stage">Pricing</span>
      {d.pricing.state === 'done' ? (
        <span className="tma-apprv-done">✓ {d.pricing.by || 'priced'}{d.pricing.at ? ' · ' + dt(d.pricing.at) : ''}</span>
      ) : <span className="tma-apprv-pending">awaiting prices</span>}
    </div>
  );
  const pending = [
    d.architecture.state !== 'approved' ? line('Poster', d.architecture, 'AI drawing…') : null,
    d.estimate.state !== 'approved' ? line('Estimate', d.estimate, 'AI estimating…') : null,
    d.pricing.state === 'awaiting' && d.estimate.state === 'approved' ? pricing : null,
    d.proposal.state !== 'approved' ? line('Proposal', d.proposal, 'AI writing…') : null,
  ].filter(Boolean);
  const history = [
    d.architecture.state === 'approved' ? line('Poster', d.architecture, '') : null,
    d.estimate.state === 'approved' ? line('Estimate', d.estimate, '') : null,
    d.pricing.state === 'done' ? pricing : null,
    d.proposal.state === 'approved' ? line('Proposal', d.proposal, '') : null,
  ].filter(Boolean);
  if (!pending.length && !history.length) return null;
  return <div className="tma-apprv">
    {pending}
    {history.length ? <details className="tma-apprv-history"><summary>Approval history <span>{history.length}</span></summary><div className="tma-apprv-history-list">{history}</div></details> : null}
  </div>;
}
